"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import type {
  HeroSectionData,
  SectionProps,
} from "@/themes/default/templates/section/types";
import { getLocalizedText } from "@/themes/default/templates/section/utils";

/**
 * epona Hero (classic) — Epona V2 banner / slider.
 *
 * A full-bleed banner: a large background photo, a deep-blue Epona
 * gradient overlay (`var(--gradient-primary)`) at reduced opacity, and
 * centred white text — an uppercase Oswald headline (`dental06-hero-title`),
 * a lead paragraph, and an optional accent-orange CTA.
 *
 * When the section carries several images/slides it auto-advances as a
 * simple cross-fading slider (client-side). With a single image (or
 * none) it renders static — the "or none" case falls back to the flat
 * Epona gradient so the band never looks empty.
 *
 * The dispatcher delegates the `split` / `overlay` hero variants to the
 * default theme, so this component only ever renders the classic look.
 */

/** Normalise the many shapes an image slot can arrive in to a URL. */
function toUrl(v: unknown): string | undefined {
  if (!v) return undefined;
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    const o = v as Record<string, any>;
    return o.url || o.image || o.src || o.backgroundImage || undefined;
  }
  return undefined;
}

/** Collect the ordered list of slide background URLs from section data. */
function collectImages(section: HeroSectionData): string[] {
  const s = section as Record<string, any>;
  const out: string[] = [];
  const push = (v: unknown) => {
    const u = toUrl(v);
    if (u) out.push(u);
  };
  if (Array.isArray(s.images)) s.images.forEach(push);
  if (Array.isArray(s.slides))
    s.slides.forEach((sl: any) => push(sl?.backgroundImage ?? sl?.image ?? sl));
  // Single-image fallbacks — only used when no explicit list was given.
  if (out.length === 0) {
    push(section.backgroundImage);
    push(s.image);
    push(s.content?.image);
  }
  return Array.from(new Set(out));
}

export function HeroSection({
  section,
  currentLanguage = "en",
}: SectionProps<HeroSectionData>) {
  const headline = section.headline
    ? getLocalizedText(section.headline, currentLanguage)
    : section.content?.title
      ? getLocalizedText(section.content.title, currentLanguage)
      : "";
  const subhead = section.subheadline
    ? getLocalizedText(section.subheadline, currentLanguage)
    : section.content?.description
      ? getLocalizedText(section.content.description, currentLanguage)
      : "";

  const buttons: any[] = Array.isArray(section.buttons)
    ? section.buttons
    : [
        section.content?.primaryButton,
        section.content?.secondaryButton,
      ].filter(Boolean);

  const images = collectImages(section);
  const hasImages = images.length > 0;

  // Tiling medical cross-hatch used as the image-less banner texture, so a
  // department without a cover photo still gets a designed slider rather
  // than a flat gradient band.
  const CROSS_PATTERN =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='44' height='44'%3E%3Cpath d='M22 14v16 M14 22h16' stroke='%23ffffff' stroke-width='1.1' stroke-linecap='round' opacity='0.16'/%3E%3C/svg%3E\")";
  const overlay = (section as any).overlay;
  const overlayEnabled = overlay?.enabled !== false;
  const overlayOpacity =
    typeof overlay?.opacity === "number" ? overlay.opacity : 0.72;

  // Auto-advancing cross-fade slider — only when there are 2+ images.
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(
      () => setActive((i) => (i + 1) % images.length),
      6000,
    );
    return () => clearInterval(id);
  }, [images.length]);

  const heightKey = ((section as any).height as string) || "large";
  const minHeight =
    heightKey === "fullscreen"
      ? "calc(100dvh - var(--site-header-h, var(--header-h-default, 130px)))"
      : heightKey === "small"
        ? "42vh"
        : heightKey === "medium"
          ? "56vh"
          : "72vh";

  const btnStyle = (style?: string): React.CSSProperties => {
    if (style === "outline")
      return {
        backgroundColor: "transparent",
        color: "#FFFFFF",
        border: "2px solid rgba(255,255,255,0.9)",
      };
    if (style === "secondary")
      return { backgroundColor: "#FFFFFF", color: "var(--color-primary)" };
    // primary (default) — gold CTA with ink label
    return { backgroundColor: "var(--color-accent)", color: "var(--color-accent-foreground)" };
  };

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{
        minHeight,
        ...(hasImages ? {} : { backgroundImage: "var(--gradient-primary)" }),
      }}
      data-section-id={section._id}
      data-section-type={section.type}
      data-hero-variant="classic"
    >
      {/* Image-less banner: keep the Epona gradient but layer a tiling
          medical cross-hatch + soft light bloom + a large faint monogram
          watermark so the slider reads as designed, not empty. */}
      {!hasImages && (
        <>
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: CROSS_PATTERN,
              backgroundSize: "44px 44px",
              maskImage:
                "radial-gradient(120% 120% at 50% 0%, #000 35%, transparent 85%)",
              WebkitMaskImage:
                "radial-gradient(120% 120% at 50% 0%, #000 35%, transparent 85%)",
            }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(70% 90% at 50% -10%, rgba(255,255,255,0.16), transparent 60%)",
            }}
            aria-hidden="true"
          />
          <span
            className="dental06-hero-title pointer-events-none select-none absolute -bottom-6 right-4 md:right-10 leading-none"
            style={{
              fontSize: "clamp(9rem, 22vw, 20rem)",
              color: "rgba(255,255,255,0.06)",
              letterSpacing: "-0.02em",
            }}
            aria-hidden="true"
          >
            {(headline || "U").trim().charAt(0).toUpperCase()}
          </span>
        </>
      )}

      {/* Background slides (cross-fade). */}
      {images.map((src, i) => (
        <div
          key={`${src}-${i}`}
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: i === active ? 1 : 0,
          }}
          aria-hidden={i !== active}
        />
      ))}

      {/* Deep-blue Epona gradient overlay for legibility over a photo. */}
      {hasImages && overlayEnabled && (
        <div
          className="absolute inset-0 pointer-events-none bg-dental06-gradient"
          style={{ opacity: overlayOpacity }}
          aria-hidden="true"
        />
      )}

      <div
        className="relative mx-auto flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center"
        style={{ maxWidth: "56rem", minHeight }}
      >
        {headline && (
          <h1 className="dental06-hero-title text-white text-3xl md:text-5xl lg:text-6xl">
            {headline}
          </h1>
        )}
        {subhead && (
          <p
            className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed"
            style={{
              color: "rgba(255,255,255,0.92)",
              fontFamily: "var(--font-sans)",
            }}
          >
            {subhead}
          </p>
        )}
        {buttons.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            {buttons.map((button, index) => (
              <Link
                key={index}
                href={button.url || "#"}
                target={button.openInNewTab ? "_blank" : undefined}
                rel={button.openInNewTab ? "noopener noreferrer" : undefined}
                className="inline-block rounded px-6 py-3 text-sm md:text-base font-semibold uppercase tracking-wide transition-opacity hover:opacity-90"
                style={btnStyle(button.style)}
              >
                {getLocalizedText(button.text, currentLanguage)}
              </Link>
            ))}
          </div>
        )}

        {/* Slider dots. */}
        {images.length > 1 && (
          <div className="mt-9 flex gap-2.5 justify-center">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === active}
                className="h-2.5 w-2.5 rounded-full transition-all"
                style={{
                  backgroundColor:
                    i === active
                      ? "var(--color-accent)"
                      : "rgba(255,255,255,0.55)",
                  width: i === active ? "1.6rem" : undefined,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default HeroSection;
